'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React from 'react';

import Button from '@/components/button/Button';
import CardWrapper from '@/components/CardWrapper.tsx';
import Badge from '@/components/label/Badge';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { FeaturedContentData } from '@/helpers/page-api-helper';

import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type FeaturedContentItemProps = {
    content: FeaturedContentData;
};

// Uses the custom link (external) when set, otherwise falls back to the content's detail page.
function FeaturedLink({
    link,
    programHref,
    className,
    ariaLabel,
    children
}: {
    link: string | null;
    programHref: HrefOrHrefWithParams<string>;
    className?: string;
    ariaLabel?: string;
    children: React.ReactNode;
}): React.JSX.Element {
    if (link) {
        return (
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ariaLabel}
                className={className}>
                {children}
            </a>
        );
    }
    return (
        <Link
            href={programHref}
            aria-label={ariaLabel}
            prefetch={false}
            className={className}>
            {children}
        </Link>
    );
}

export default function FeaturedContentItem({ content }: FeaturedContentItemProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();

    const title = content.title;
    // `posterImage` already starts from the movie's W780 poster, so the old
    // `posterImages[W780] || posterImage` was the same value twice.
    const posterImage = content.posterImage;
    // W1280 rather than the resolved `backdropImage` (W780): this card is a full-width 300px hero on
    // desktop, where W780 is visibly soft.
    const backdropImage = content.backdropImageURL_W1280 || content.posterImage;
    const highlightText = content.highlightText;

    const programHref = {
        pathname: '/program/[contentId]',
        params: { contentId: content.id }
    } as HrefOrHrefWithParams<string>;

    // Badge text: custom featureBadge if set, otherwise the translated reason.
    const badgeText =
        content.featureBadge || (content.reason ? t(`cinema-featured-content.reason.${content.reason}`) : null);
    const badge = content.showFeatureBadge && badgeText && (
        <Badge
            name={badgeText}
            style={{
                backgroundColor: domainConfiguration.style.primaryColor,
                color: domainConfiguration.style.onPrimaryColor
            }}
        />
    );

    // Without a CTA button the whole card is the link; with a button, only the button is clickable.

    return (
        <CardWrapper
            className="group relative w-full flex flex-col lg:flex-row lg:h-[300px] overflow-hidden rounded-8 border-2"
            style={{ borderColor: content.highlightColor || 'var(--primary-color)' }}
            domainConfiguration={domainConfiguration}>
            {/* Stretched overlay link — makes the whole card clickable when there's no CTA button */}
            {!content.showButton && (
                <FeaturedLink
                    link={content.link}
                    programHref={programHref}
                    ariaLabel={title}
                    className="absolute inset-0 z-[1]">
                    <span className="sr-only">{title}</span>
                </FeaturedLink>
            )}

            {/* Poster header: backdrop + gradient + overlaid poster + (mobile) badge */}
            <div className="relative flex-none h-[190px] w-full lg:h-full lg:flex-1">
                {/* Wrapper carries the gradient mask so it stays fixed while only the image scales on hover */}
                <div className="w-full h-full overflow-hidden gradient-mask-b-90 lg:gradient-mask-r-90">
                    <ImageWithTransition
                        className="w-full h-full object-cover group-hover:scale-105 duration-200"
                        src={backdropImage ?? ''}
                        alt={`${title} Backdrop`}
                        width={1000}
                        height={500}
                    />
                </div>
                {/* Poster overlay (top-left) */}
                <div className="absolute left-12 top-12 lg:left-16 lg:top-16 h-[154px] lg:h-[268px] aspect-[2/3] overflow-hidden rounded-4">
                    <ImageWithTransition
                        className="w-full h-full object-cover"
                        src={posterImage ?? ''}
                        alt={`${title} Poster`}
                        width={179}
                        height={269}
                    />
                </div>

                {/* Badge — mobile shows it on the poster header */}
                {badge && <div className="absolute right-12 top-12 lg:hidden">{badge}</div>}
            </div>

            {/* Text box */}
            <div className="relative flex flex-none flex-col p-12 lg:w-[560px]">
                {/* Badge — desktop shows it top-right of the text box */}
                {badge && <div className="hidden justify-end lg:flex">{badge}</div>}

                <div
                    className={classnames(
                        'flex w-full flex-col gap-16 text-[var(--on-background-color)]',
                        'lg:my-auto lg:max-w-[390px] lg:self-center'
                    )}>
                    <div className="flex flex-col gap-4">
                        {/* Two lines rather than a single truncated one — long titles stay readable, and
                            the desktop card's fixed 300px height still holds with the highlight text below. */}
                        <h3 className="text-body-bold-16 line-clamp-2">{title}</h3>
                        {highlightText && (
                            <p className="text-body-14 leading-[24px] opacity-80 line-clamp-3 lg:line-clamp-5">
                                {highlightText}
                            </p>
                        )}
                    </div>

                    {content.showButton && (
                        // Button wrapped in FeaturedLink so it stays a real (crawlable, new-tab-able)
                        // link like before, while using the shared Button component for the CTA.
                        <FeaturedLink
                            link={content.link}
                            programHref={programHref}
                            className="relative z-[2] block">
                            <Button
                                className="w-full"
                                type="button"
                                css={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}>
                                {content.buttonTitle ?? t('home.cinema-featured-content.get-tickets')}
                            </Button>
                        </FeaturedLink>
                    )}
                </div>
            </div>
        </CardWrapper>
    );
}
